import { useState } from 'react';
import { useDonasiList, useDeleteDonasi } from '@/hooks/useDonasi';
import { donasiApi } from '@/api/donasi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BulkSelectionToolbar } from '@/components/list/BulkSelectionToolbar';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import {
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Donasi } from '@/types';

export function DonasiList() {
  const [page, setPage] = useState(1);
  // const today = new Date().toISOString().slice(0, 10);
  // const [date, setTanggal] = useState(today);

  // filter range tanggal
  const getLocalDate = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 10);
  };

  const [startDate, setStartDate] = useState(getLocalDate());
  const [endDate, setEndDate] = useState(getLocalDate());

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string>('all');
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);

  const statusMappings = [
    {
      app: 'Selesai Dibayar',
      variant: 'default' as const,
      description: 'Transaksi berhasil dan dana sudah masuk.',
    },
    {
      app: 'Diproses',
      variant: 'outline' as const,
      description: 'Pembayaran masih diproses atau menunggu konfirmasi.',
    },
    {
      app: 'Belum Terbayar',
      variant: 'secondary' as const,
      description: 'Transaksi sudah dibuat, tetapi belum dibayar.',
    },
    {
      app: 'Expired',
      variant: 'destructive' as const,
      description: 'Transaksi batal, kedaluwarsa, atau tidak berhasil diproses.',
    },
  ];

  const { data, isLoading } = useDonasiList({
    page,
    limit: 10,
    // date: date || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    status: statusFilter === 'all' ? undefined : Number(statusFilter),
    transactionType: transactionTypeFilter === 'all' ? undefined : transactionTypeFilter,
  });
  const deleteDonasi = useDeleteDonasi();
  const selection = useBulkSelection((data?.data || []).map((item) => item.id));

  const handleHapus = async () => {
    if (pendingDeleteIds.length) {
      for (const id of pendingDeleteIds) {
        await deleteDonasi.mutateAsync(id);
      }
      selection.clearSelection();
      setPendingDeleteIds([]);
    }
  };

  const getStatusBadge = (status?: number) => {
    const variants: Record<number, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      1: 'default',
      2: 'destructive',
      3: 'secondary',
      4: 'outline',
    };
    const labels: Record<number, string> = {
      1: 'Selesai Dibayar',
      2: 'Expired',
      3: 'Belum Terbayar',
      4: 'Diproses',
    };
    const resolvedStatus = status ?? 3;
    return <Badge variant={variants[resolvedStatus] ?? 'secondary'}>{labels[resolvedStatus] ?? 'Belum Terbayar'}</Badge>;
  };

  const getTransactionTypeLabel = (transactionType?: string) => {
    if (transactionType === 'withdrawal') {
      return 'Penarikan';
    }

    return 'Pembayaran';
  };

  const handleExportSettlement = async () => {
    const blob = await donasiApi.exportSettlementSummary({
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `settlement-report-${startDate || 'all'}-${endDate || 'all'}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Transaksi</h2>
          <p className="text-muted-foreground">
            Kelola transaksi dan pantau status pembayaran.
          </p>
        </div>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Tanggal Mulai</div>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setPage(1);
                  setStartDate(e.target.value);
                }}
              />
            </div>

            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Tanggal Selesai</div>
              <Input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => {
                  setPage(1);
                  setEndDate(e.target.value);
                }}
              />
            </div>
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Status Pembayaran</div>
              <Select
                value={statusFilter}
                onValueChange={(value) => {
                  setPage(1);
                  setStatusFilter(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Semua Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Status</SelectItem>
                  <SelectItem value="3">Belum Terbayar</SelectItem>
                  <SelectItem value="4">Diproses</SelectItem>
                  <SelectItem value="1">Selesai Dibayar</SelectItem>
                  <SelectItem value="2">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">Jenis Transaksi</div>
              <Select
                value={transactionTypeFilter}
                onValueChange={(value) => {
                  setPage(1);
                  setTransactionTypeFilter(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Semua Jenis" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Jenis</SelectItem>
                  <SelectItem value="withdrawal">Penarikan</SelectItem>
                  <SelectItem value="payment">Pembayaran</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button type="button" variant="outline" onClick={handleExportSettlement} className="gap-2">
                <FileSpreadsheet className="h-4 w-4" />
                Export Settlement XLS
              </Button>
            </div>
          </div>

          <div className="mt-6 rounded-lg border bg-muted/30 p-4">
            <div className="text-sm font-semibold">Keterangan Status</div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {statusMappings.map((item) => (
                <div key={item.app} className="rounded-md border bg-background p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={item.variant}>{item.app}</Badge>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground">{item.description}</div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <BulkSelectionToolbar
            className="m-4 mb-0"
            selectedCount={selection.selectedCount}
            itemLabel="transaksi"
            onClear={selection.clearSelection}
            onDelete={() => setPendingDeleteIds(selection.selectedIds)}
            deleteDisabled={deleteDonasi.isPending}
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox checked={selection.allSelected} onCheckedChange={(checked) => selection.toggleAll(Boolean(checked))} />
                </TableHead>
                <TableHead>ID</TableHead>
                <TableHead>Kode Invoice</TableHead>
                <TableHead>Nama Donatur</TableHead>
                <TableHead>Nilai</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Dibuat Pada</TableHead>
                <TableHead>Jenis Transaksi</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8">
                    Memuat...
                  </TableCell>
                </TableRow>
              ) : data?.data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-8">
                    Belum ada transaksi.
                  </TableCell>
                </TableRow>
              ) : (
                data?.data.map((donasi: Donasi) => (
                  <TableRow key={donasi.id} className={selection.isSelected(donasi.id) ? 'bg-muted/30' : undefined}>
                    <TableCell>
                      <Checkbox checked={selection.isSelected(donasi.id)} onCheckedChange={(checked) => selection.toggleOne(donasi.id, Boolean(checked))} />
                    </TableCell>
                    <TableCell>{donasi.id}</TableCell>
                    <TableCell>{donasi.kodeInv || '-'}</TableCell>
                    <TableCell>{donasi.donaturName || '-'}</TableCell>
                    <TableCell>
                      Rp {donasi.nilai?.toLocaleString('id-ID')}
                    </TableCell>
                    <TableCell>{getStatusBadge(donasi.status)}</TableCell>
                    <TableCell>
                      {donasi.createdAt
                        ? new Date(donasi.createdAt).toLocaleDateString('id-ID')
                        : '-'}
                    </TableCell>
                    <TableCell>{getTransactionTypeLabel(donasi.transactionType)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/donasi/${donasi.id}`}>
                              <Eye className="mr-2 h-4 w-4" />
                              Lihat
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild>
                            <Link to={`/donasi/${donasi.id}/edit`}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => setPendingDeleteIds([donasi.id])}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {!isLoading && data?.data.length ? (
                <TableRow className="bg-muted/30 font-semibold">
                  <TableCell colSpan={4} className="text-right">
                    Total
                  </TableCell>
                  <TableCell>
                    Rp {(data.totalAmount || 0).toLocaleString('id-ID')}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm text-muted-foreground">
          Halaman {page} dari {Math.ceil((data?.total || 0) / 10)}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage((p) => p + 1)}
          disabled={page >= Math.ceil((data?.total || 0) / 10)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Delete Dialog */}
      <Dialog open={pendingDeleteIds.length > 0} onOpenChange={() => setPendingDeleteIds([])}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Yakin ingin melanjutkan?</DialogTitle>
            <DialogDescription>
              Tindakan ini tidak dapat dibatalkan. This will permanently delete the
              data transaksi.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDeleteIds([])}>
              Batal
            </Button>
            <Button variant="destructive" onClick={handleHapus}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
